import type { ApiTypes, EventType, Schemas } from "@fluid-app/api-client";
import { EventTypes } from "@fluid-app/api-client";

import type { FluidConfig } from "./types";
import { FluidCore } from "./core/FluidCore";

// Re-export types and the EventType enum value needed by function signatures
export type { ApiTypes, Schemas, EventType };
export { EventTypes };

// Get the singleton instance internally
const fluidCore = FluidCore.getInstance();

/**
 * Initialize the Fluid SDK
 * @param config Configuration options
 */
export function initializeFluid(config: FluidConfig) {
  fluidCore.initializeFluid(config);
}

/**
 * Get the current cart from the API.
 * @returns Promise resolving to the current cart or undefined if not found.
 */
export function getCart() {
  return fluidCore.getCart();
}

/**
 * Clear the cart
 */
export function clearCart() {
  fluidCore.clearCart();
}

/**
 * Reset the SDK state
 * This will clear all stored data including cart and user data
 */
export function reset() {
  fluidCore.reset();
}

/**
 * Proceed to checkout, waiting for all pending cart operations to complete
 * @returns Promise that resolves when checkout process is complete
 */
export function checkout() {
  return fluidCore.checkout();
}

/**
 * Get the cart data from local storage without making an API call
 * This is useful for optimistic updates
 * @returns The cart data from local storage or null if not found
 */
export function getLocalCart() {
  return fluidCore.getLocalCart();
}

/**
 * Add/Increment the quantity of item(s) in the cart.
 * If an item is not in the cart, it will be added with the specified increment quantity.
 * @param variantIdOrItems Either a single variant ID or an array of items to add/increment.
 * @param options Optional parameters, only used when variantIdOrItems is a number:
 *   - quantity: The quantity to add/increment by (defaults to 1).
 *   - subscribe: Whether to add this item as a subscription (defaults to false).
 * @returns Promise resolving to the updated cart, or undefined on failure.
 */
export function addCartItems(
  variantIdOrItems:
    | number
    | { variant_id: number; quantity: number; subscribe?: boolean }[],
  options?: { quantity?: number; subscribe?: boolean },
) {
  let itemsToIncrement: {
    variant_id: number;
    quantity: number;
    subscribe?: boolean;
  }[];

  if (typeof variantIdOrItems === "number") {
    const quantityToAdd = options?.quantity ?? 1;
    if (quantityToAdd <= 0) {
      console.warn("Quantity to add must be positive.");
      return Promise.resolve(undefined);
    }
    itemsToIncrement = [
      {
        variant_id: variantIdOrItems,
        quantity: quantityToAdd,
        subscribe: options?.subscribe,
      },
    ];
  } else {
    itemsToIncrement = variantIdOrItems;
  }

  const fluidCore = FluidCore.getInstance();
  // Call the *internal* incrementCartItem method
  return fluidCore.incrementCartItem(itemsToIncrement);
}

/**
 * Remove/Decrement the quantity of an item in the cart by 1 (or specified amount).
 * If the quantity becomes 0 or less, the item is removed from the cart.
 * @param variantId The variant ID of the item to remove/decrement.
 * @param options Optional parameters:
 *   - quantity: The quantity to remove/decrement by (defaults to 1).
 * @returns Promise resolving to the updated cart, or undefined if the item was not found or on failure.
 */
export function removeCartItem(
  variantId: number,
  options?: { quantity?: number },
) {
  const quantityToRemove = options?.quantity ?? 1;
  const fluidCore = FluidCore.getInstance();
  // Call the *internal* decrementCartItem method
  return fluidCore.decrementCartItem(variantId, quantityToRemove);
}

export function updateCartItems(
  items: { variant_id: number; quantity: number }[],
) {
  const fluidCore = FluidCore.getInstance();
  return fluidCore.updateCartItems(items);
}

/**
 * Add an enrollment pack to the cart
 * @param enrollmentPackId The ID of the enrollment pack to add
 * @param options Optional parameters:
 *   - event: Custom event type to dispatch (defaults to ADD_TO_CART)
 * @returns Promise resolving to the updated cart, or undefined on failure
 */
export function addEnrollmentPack(
  enrollmentPackId: number,
  options?: { event?: EventType },
) {
  const fluidCore = FluidCore.getInstance();
  return fluidCore.addEnrollmentPack({
    enrollmentPackId,
    event: options?.event,
  });
}

/**
 * Subscribe to a cart item for recurring purchases
 * @param itemId The ID of the item to subscribe to
 * @returns Promise resolving to the updated cart, or undefined on failure
 */
export function subscribeCartItem(itemId: number) {
  const fluidCore = FluidCore.getInstance();
  return fluidCore.subscribeCartItem(itemId);
}

/**
 * Unsubscribe from a cart item, reverting it to a one-time purchase
 * @param itemId The ID of the item to unsubscribe from
 * @returns Promise resolving to the updated cart, or undefined on failure
 */
export function unsubscribeCartItem(itemId: number) {
  const fluidCore = FluidCore.getInstance();
  return fluidCore.unsubscribeCartItem(itemId);
}

/**
 * Get the current number of items in the cart
 * @returns The number of items in the cart
 */
export function getCartItemCount() {
  const fluidCore = FluidCore.getInstance();
  return fluidCore.getCartItemCount();
}

/**
 * Update locale settings for the cart
 * @param options The locale settings to update
 * @returns Promise resolving to undefined
 */
export function updateLocaleSettings(options: {
  language?: string;
  country?: string;
}) {
  const fluidCore = FluidCore.getInstance();
  return fluidCore.updateLocaleSettings(options);
}

/**
 * Submit lead capture form data to the API
 *
 * @param formData Lead capture form data with contact information and message
 * @returns Promise resolving to the API response
 */
export function captureLead(
  formData: ApiTypes["lead-capture"]["request"]["lead"],
) {
  const fluidCore = FluidCore.getInstance();
  return fluidCore.captureLead(formData);
}

/**
 * Fetch settings from the API
 * @returns Promise resolving to the settings data
 */
export function fetchSettings() {
  const fluidCore = FluidCore.getInstance();
  return fluidCore.fetchSettings();
}

/**
 * Get settings from the API
 * @returns The settings data
 */
export function getSettings() {
  const fluidCore = FluidCore.getInstance();
  return fluidCore.getSettings();
}

/**
 * Get or fetch settings from the API
 * @returns The settings data
 */
export function getOrFetchSettings() {
  const fluidCore = FluidCore.getInstance();
  return fluidCore.getOrFetchSettings();
}

/**
 * Remove an enrollment pack from the cart
 * @param enrollmentPackId The ID of the enrollment pack to remove
 * @returns Promise that resolves when the enrollment pack is removed
 */
export function removeEnrollmentPack(enrollmentPackId: number) {
  const fluidCore = FluidCore.getInstance();
  return fluidCore.removeEnrollmentPack(enrollmentPackId);
}

// Re-export beacon flush function from fairshare for convenience
export { flushEventsWithBeacon } from "@fluid-app/fairshare";

/**
 * Look up affiliate information by attribution data
 * @param attribution The attribution object containing the lookup criteria (email, username, share_guid, fluid_rep_id, or external_id)
 * @returns Promise resolving to the affiliate lookup response
 */
export function lookupAffiliate(attribution: Schemas["Attribution"]) {
  const fluidCore = FluidCore.getInstance();
  return fluidCore.lookupAffiliate(attribution);
}
